/**
 * "Cut to size" standalone page: Material -> Colour -> Thickness -> a list
 * of custom part sizes, each bounds-checked (with rotation) against the
 * largest stock sheet available at that thickness. No nesting/packing here
 * by design - just one independent preview per distinct size entered.
 *
 * Materials tagged CTSShape get extra steps between Thickness and Parts:
 * Shape (Rectangle/Circle) -> Rounded corners (rectangle only, Yes/No) ->
 * Drilled holes (both shapes, Yes/No) -> Select cut (Saw/CNC/Laser,
 * eligible options depend on shape, rounded corners and thickness - laser
 * never offered at 10mm+). These all happen *before* Parts, since Edge
 * finish (Saw/Milled/Diamond polish/Diamond polish & buff) is a *per-row*
 * choice inside the sizes table, and its eligible options depend on
 * Select cut - Parts can't be entered until that's already known.
 *
 * Pricing: each part is priced independently, at a rate derived from the
 * chosen thickness's largest stock sheet - (sheet price / sheet area in
 * m2) x (1 + uplift%) for material (the uplift covers cutting too, so
 * there's no separate machining charge here), plus perimeter(m) x
 * thickness(mm) x rate for its edge finish (zero for Saw/Milled), plus
 * (for rectangle parts with rounded corners) a per-corner price and (for
 * any part with drilled holes) a per-hole price, each scaling linearly
 * with thickness between its own two configured thickness/price pairs.
 * All parts are summed into one total, added to the cart as a single
 * line via the Cut to Size charge product ladder
 * (assets/cut-to-size-charge.js), with material/colour/thickness/shape/
 * rounded corners/drilled holes/cut method/real product and a per-part
 * breakdown (incl. each part's own edge finish, rounded corners and
 * drilled holes) carried as line item properties.
 *
 * Rounded corners is a per-part choice too, when the top-level "Rounded
 * corners?" answer is Yes: each row gets its own popover (a compact
 * version of the merchant's existing 4-corner diagram) to pick which of
 * the part's four corners are rounded and a shared radius for that part -
 * radius is informational only (a cart line property), it doesn't affect
 * price.
 *
 * Drilled holes works the same way, when the top-level "Drilled holes?"
 * answer is Yes (for either shape): each row gets its own popover with a
 * 3x3 grid of 9 positions (corners/edge-midpoints/centre for a rectangle;
 * 8 compass points at 45deg increments from 12 o'clock plus centre for a
 * circle) plus a shared diameter, distance from edge (ignored for the
 * centre position) and countersunk Yes/No for that part. Distance from
 * edge is a straight-line inset from the nearest straight edge for a
 * rectangle, or a radial inset from the circumference for a circle.
 *
 * Depends on:
 *  - window.CutToSizeSettings (rendered by sections/cut-to-size.liquid):
 *      { indexUrl, minCut, upliftPercent, diamondPolishRate,
 *        diamondPolishBuffRate, cornerRadiusMin, cornerRadiusMax,
 *        cornerRateMinThickness, cornerRateMaxThickness, cornerRateMin,
 *        cornerRateMax, holeDiameterMin, holeDiameterMax, holeDistanceMin,
 *        holeDistanceMax, holeRateMinThickness, holeRateMaxThickness,
 *        holeRateMin, holeRateMax, labels: { ... } }
 *  - assets/cut-to-size-index.json (material -> colour list -> product handle),
 *    regenerated by scripts/generate-cut-to-size-index.js
 *  - window.CutToSizeChargeTable (rendered by snippets/cut-to-size-charge-table.liquid)
 *  - window.CutToSizeCharge (assets/cut-to-size-charge.js)
 */
(function () {
  'use strict';

  var BADGE_COLORS = ['#29A470', '#2563EB', '#F59E0B', '#DC2626', '#7C3AED', '#0891B2', '#DB2777', '#65A30D'];
  var PREVIEW_MAX_PX = 280;

  function badgeForIndex(index) {
    var letter = String.fromCharCode(65 + (index % 26));
    if (index >= 26) letter += Math.floor(index / 26);
    return { letter: letter, color: BADGE_COLORS[index % BADGE_COLORS.length] };
  }

  function findOptionPosition(options, matcher) {
    for (var i = 0; i < options.length; i++) {
      if (matcher(options[i].name.toLowerCase())) return i + 1; // option1/2/3 are 1-indexed
    }
    return 0;
  }

  /**
   * Largest available stock sheet for a given thickness: the variant with
   * the greatest length, then (among variants sharing that same max length)
   * the greatest width - never picked by area, per the confirmed catalog
   * convention that length is always the longer side. Returns that
   * variant too (not just its dimensions), since its price is what the
   * per-square-metre rate is derived from.
   */
  function largestSheetForThickness(product, thicknessPos, lengthPos, widthPos, thicknessValue) {
    var thicknessKey = 'option' + thicknessPos;
    var lengthKey = 'option' + lengthPos;
    var widthKey = 'option' + widthPos;

    var atThickness = product.variants.filter(function (v) {
      return v[thicknessKey] === thicknessValue;
    });
    if (atThickness.length === 0) return null;

    var maxLength = Math.max.apply(
      null,
      atThickness.map(function (v) {
        return parseFloat(v[lengthKey]) || 0;
      })
    );
    var atMaxLength = atThickness.filter(function (v) {
      return (parseFloat(v[lengthKey]) || 0) === maxLength;
    });
    var maxWidth = Math.max.apply(
      null,
      atMaxLength.map(function (v) {
        return parseFloat(v[widthKey]) || 0;
      })
    );
    var variant = atMaxLength.filter(function (v) {
      return (parseFloat(v[widthKey]) || 0) === maxWidth;
    })[0];

    return { length: maxLength, width: maxWidth, variant: variant };
  }

  /**
   * GBP per square metre for the currently selected thickness: the largest
   * sheet's own price divided by its area, marked up by the configured
   * uplift (which covers cutting, not just material). Null if there's no
   * selection yet or the sheet has no usable area.
   */
  function ratePerM2(bounds, upliftPercent) {
    if (!bounds || !bounds.variant) return null;
    var sheetAreaM2 = (bounds.length / 1000) * (bounds.width / 1000);
    if (!(sheetAreaM2 > 0)) return null;
    return (bounds.variant.price / 100 / sheetAreaM2) * (1 + upliftPercent / 100);
  }

  /**
   * GBP price for one piece (all its quantity included) at the given rate.
   */
  function piecePriceGBP(piece, ratePerM2Value) {
    var areaM2 = (piece.length / 1000) * (piece.width / 1000);
    return ratePerM2Value * areaM2 * piece.qty;
  }

  /**
   * Perimeter of one piece, in metres - the full cut edge that gets
   * finished. A rectangle's is its four sides; a circle's is its
   * circumference (length and width are the same diameter internally).
   */
  function perimeterM(piece) {
    if (piece.shape === 'circle') return (Math.PI * piece.length) / 1000;
    return (2 * (piece.length + piece.width)) / 1000;
  }

  /**
   * GBP price for one piece's edge finish (all its quantity included) -
   * zero for Saw edge, Milled edge, or no finish chosen. Diamond polish and
   * Diamond polish & buff are priced by perimeter (m) x thickness (mm) x
   * their own configurable rate.
   */
  function edgeFinishPriceGBP(piece, thicknessValue, diamondPolishRate, diamondPolishBuffRate) {
    var rate = 0;
    if (piece.edgeFinish === 'diamond') rate = diamondPolishRate;
    if (piece.edgeFinish === 'diamond-buff') rate = diamondPolishBuffRate;
    if (!rate) return 0;
    return perimeterM(piece) * thicknessValue * rate * piece.qty;
  }

  /**
   * How many of a piece's four corners are marked as rounded.
   */
  function countRoundedCorners(corners) {
    if (!corners) return 0;
    return ['tl', 'tr', 'bl', 'br'].reduce(function (sum, key) {
      return sum + (corners[key] ? 1 : 0);
    }, 0);
  }

  /**
   * GBP price per rounded corner at a given thickness - scales linearly
   * between two configured thickness/price pairs (e.g. £1.50 at 1.5mm up
   * to £3.50 at 20mm), clamped to that price range for any thickness
   * outside it rather than extrapolating past the configured bounds.
   */
  function cornerRateGBP(thicknessValue, cornerSettings) {
    var minT = cornerSettings.minThickness;
    var maxT = cornerSettings.maxThickness;
    var minRate = cornerSettings.minRate;
    var maxRate = cornerSettings.maxRate;
    var frac = maxT === minT ? 0 : (thicknessValue - minT) / (maxT - minT);
    frac = Math.max(0, Math.min(1, frac));
    return minRate + (maxRate - minRate) * frac;
  }

  /**
   * GBP price for one piece's rounded corners (all its quantity included) -
   * zero when the piece has no rounded corners at all.
   */
  function roundedCornersPriceGBP(piece, thicknessValue, cornerSettings) {
    var count = countRoundedCorners(piece.corners);
    if (!count) return 0;
    return cornerRateGBP(thicknessValue, cornerSettings) * count * piece.qty;
  }

  var HOLE_POSITIONS = ['tl', 'tm', 'tr', 'ml', 'mc', 'mr', 'bl', 'bm', 'br'];

  /**
   * How many of a piece's nine hole positions are marked as drilled.
   */
  function countSelectedHoles(holes) {
    if (!holes) return 0;
    return HOLE_POSITIONS.reduce(function (sum, key) {
      return sum + (holes[key] ? 1 : 0);
    }, 0);
  }

  /**
   * GBP price per drilled hole at a given thickness - same linear
   * thickness/price interpolation as rounded corners, just its own
   * configured range (e.g. £1 at 1.5mm up to £3 at 20mm).
   */
  function holeRateGBP(thicknessValue, holeSettings) {
    var minT = holeSettings.minThickness;
    var maxT = holeSettings.maxThickness;
    var minRate = holeSettings.minRate;
    var maxRate = holeSettings.maxRate;
    var frac = maxT === minT ? 0 : (thicknessValue - minT) / (maxT - minT);
    frac = Math.max(0, Math.min(1, frac));
    return minRate + (maxRate - minRate) * frac;
  }

  /**
   * GBP price for one piece's drilled holes (all its quantity included) -
   * zero when the piece has no drilled holes at all. Diameter, distance
   * from edge and countersunk don't affect price - only the hole count
   * and thickness do, same as rounded corners' radius being price-neutral.
   */
  function drilledHolesPriceGBP(piece, thicknessValue, holeSettings) {
    var count = countSelectedHoles(piece.holes);
    if (!count) return 0;
    return holeRateGBP(thicknessValue, holeSettings) * count * piece.qty;
  }

  /**
   * SVG path 'd' for a rectangle whose four corners can each be square or
   * rounded independently - a plain <rect rx> can't do this, since its
   * rx/ry apply uniformly to all four corners. A zero radius on any given
   * corner degenerates its arc to a straight line (per the SVG spec), so
   * the same path shape works whether a corner is rounded or square.
   */
  function roundedRectPath(width, height, tl, tr, br, bl) {
    return (
      'M' + tl + ',0' +
      ' L' + (width - tr) + ',0' +
      ' A' + tr + ',' + tr + ' 0 0 1 ' + width + ',' + tr +
      ' L' + width + ',' + (height - br) +
      ' A' + br + ',' + br + ' 0 0 1 ' + (width - br) + ',' + height +
      ' L' + bl + ',' + height +
      ' A' + bl + ',' + bl + ' 0 0 1 0,' + (height - bl) +
      ' L0,' + tl +
      ' A' + tl + ',' + tl + ' 0 0 1 ' + tl + ',0' +
      ' Z'
    );
  }

  /**
   * Draws one technical-drawing-style dimension line (a straight line with
   * a small triangular arrowhead at each end, pointing outward) plus a
   * measurement label offset to the outside of it by labelGap, into an
   * existing <svg>. Used for the length/diameter line above a piece and
   * the width line down its left side - both are always axis-aligned
   * (never diagonal), so the arrowheads are simplified to horizontal or
   * vertical only, and the label sits further from the shape than the
   * line (never on top of it) by moving further in the same outward
   * direction the line's own arrows point.
   */
  function appendDimensionLine(svg, x1, y1, x2, y2, labelText, vertical, labelGap) {
    var ns = 'http://www.w3.org/2000/svg';
    var group = document.createElementNS(ns, 'g');
    group.setAttribute('class', 'cts__dimension');

    var line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', x1);
    line.setAttribute('y1', y1);
    line.setAttribute('x2', x2);
    line.setAttribute('y2', y2);
    group.appendChild(line);

    var a = 4; // arrowhead size, in px
    function arrowhead(tipX, tipY, outwardX, outwardY) {
      var path = document.createElementNS(ns, 'path');
      var d =
        outwardY === 0
          ? 'M' + tipX + ',' + tipY + ' L' + (tipX - outwardX * a) + ',' + (tipY - a) + ' L' + (tipX - outwardX * a) + ',' + (tipY + a) + ' Z'
          : 'M' + tipX + ',' + tipY + ' L' + (tipX - a) + ',' + (tipY - outwardY * a) + ' L' + (tipX + a) + ',' + (tipY - outwardY * a) + ' Z';
      path.setAttribute('d', d);
      group.appendChild(path);
    }
    if (vertical) {
      arrowhead(x1, y1, 0, -1);
      arrowhead(x2, y2, 0, 1);
    } else {
      arrowhead(x1, y1, -1, 0);
      arrowhead(x2, y2, 1, 0);
    }

    var midX = (x1 + x2) / 2;
    var midY = (y1 + y2) / 2;
    var label = document.createElementNS(ns, 'text');
    label.setAttribute('class', 'cts__dimension-label');
    if (vertical) {
      label.setAttribute('x', midX - labelGap);
      label.setAttribute('y', midY);
      label.setAttribute('transform', 'rotate(-90 ' + (midX - labelGap) + ' ' + midY + ')');
    } else {
      label.setAttribute('x', midX);
      label.setAttribute('y', midY - labelGap);
    }
    label.textContent = labelText;
    group.appendChild(label);

    svg.appendChild(group);
  }

  /**
   * 12-o'clock-clockwise compass angle (degrees) for each of the 8
   * non-centre hole positions on a circle, per the merchant's own mapping:
   * 0=12 o'clock (Top middle), 45=Top right, 90=Middle right (3 o'clock),
   * 135=Bottom right, 180=Bottom middle (6 o'clock), 225=Bottom left,
   * 270=Middle left (9 o'clock), 315=Top left.
   */
  var CIRCLE_HOLE_ANGLES = { tm: 0, tr: 45, mr: 90, br: 135, bm: 180, bl: 225, ml: 270, tl: 315 };

  /**
   * Preview-pixel {x, y} for each selected hole position on a piece, for
   * whichever shape it is. Rectangle positions are inset from their
   * nearest straight edge(s) by distancePx; circle positions are inset
   * radially from the circumference by the same distancePx, at the
   * compass angle above (Middle centre is always the true centre for
   * either shape, ignoring distance).
   */
  function holePositionsPx(piece, pxW, pxH, scale) {
    var positions = {};
    if (!piece.holes) return positions;

    var distancePx = piece.holeDistance ? Math.min(piece.holeDistance * scale, Math.min(pxW, pxH) / 2) : 0;

    if (piece.shape === 'circle') {
      var cx = pxW / 2;
      var cy = pxH / 2;
      var outerR = Math.min(pxW, pxH) / 2;
      var holeR = Math.max(outerR - distancePx, 0);
      Object.keys(CIRCLE_HOLE_ANGLES).forEach(function (key) {
        if (!piece.holes[key]) return;
        var rad = (CIRCLE_HOLE_ANGLES[key] * Math.PI) / 180;
        positions[key] = { x: cx + holeR * Math.sin(rad), y: cy - holeR * Math.cos(rad) };
      });
      if (piece.holes.mc) positions.mc = { x: cx, y: cy };
    } else {
      // A rounded corner cuts the material back diagonally, along a
      // quarter-circle fillet - a hole inset by the same distance from
      // both straight edges as a square corner would use can fall outside
      // that curved edge once the radius is large enough (the point (d,d)
      // is only actually inside the fillet once d >= radius*(1-1/sqrt(2))).
      // Clamps each corner's own inset up to that minimum, but only for
      // corners the customer actually rounded - unrounded corners and the
      // non-corner positions (edge midpoints, centre) are never affected.
      var cornerRadiusPx = piece.cornerRadius ? Math.min(Math.max(4, piece.cornerRadius * scale), pxW / 2, pxH / 2) : 0;
      var minCornerDistancePx = cornerRadiusPx * (1 - 1 / Math.SQRT2);
      function cornerDistancePx(isRounded) {
        return isRounded ? Math.max(distancePx, minCornerDistancePx) : distancePx;
      }
      var tlDist = cornerDistancePx(piece.corners && piece.corners.tl);
      var trDist = cornerDistancePx(piece.corners && piece.corners.tr);
      var blDist = cornerDistancePx(piece.corners && piece.corners.bl);
      var brDist = cornerDistancePx(piece.corners && piece.corners.br);

      if (piece.holes.tl) positions.tl = { x: tlDist, y: tlDist };
      if (piece.holes.tm) positions.tm = { x: pxW / 2, y: distancePx };
      if (piece.holes.tr) positions.tr = { x: pxW - trDist, y: trDist };
      if (piece.holes.ml) positions.ml = { x: distancePx, y: pxH / 2 };
      if (piece.holes.mc) positions.mc = { x: pxW / 2, y: pxH / 2 };
      if (piece.holes.mr) positions.mr = { x: pxW - distancePx, y: pxH / 2 };
      if (piece.holes.bl) positions.bl = { x: blDist, y: pxH - blDist };
      if (piece.holes.bm) positions.bm = { x: pxW / 2, y: pxH - distancePx };
      if (piece.holes.br) positions.br = { x: pxW - brDist, y: pxH - brDist };
    }

    return positions;
  }

  function fitsWithRotation(length, width, boundsLength, boundsWidth) {
    var direct = length <= boundsLength + 1e-9 && width <= boundsWidth + 1e-9;
    var rotated = width <= boundsLength + 1e-9 && length <= boundsWidth + 1e-9;
    return direct || rotated;
  }

  function init() {
    var root = document.querySelector('[data-cts-root]');
    var settings = window.CutToSizeSettings;
    if (!root || !settings) return;

    var materialGrid = root.querySelector('[data-cts-material-grid]');
    var colourStep = root.querySelector('[data-cts-step="colour"]');
    var colourGrid = root.querySelector('[data-cts-colour-grid]');
    var thicknessStep = root.querySelector('[data-cts-step="thickness"]');
    var thicknessGrid = root.querySelector('[data-cts-thickness-grid]');
    var shapeStep = root.querySelector('[data-cts-step="shape"]');
    var shapePills = root.querySelectorAll('[data-cts-shape]');
    var rectangleTemplate = document.querySelector('[data-cts-row-template="rectangle"]');
    var circleTemplate = document.querySelector('[data-cts-row-template="circle"]');
    var partsStep = root.querySelector('[data-cts-step="parts"]');
    var boundsNoteEl = root.querySelector('[data-cts-bounds-note]');
    var rowsBody = root.querySelector('[data-cts-rows]');
    var addRowButton = root.querySelector('[data-cts-add-row]');
    var errorEl = root.querySelector('[data-cts-error]');
    var previewsEl = root.querySelector('[data-cts-previews]');
    var roundedCornersStep = root.querySelector('[data-cts-step="rounded-corners"]');
    var roundedCornersPills = root.querySelectorAll('[data-cts-rounded-corners]');
    var drilledHolesStep = root.querySelector('[data-cts-step="drilled-holes"]');
    var drilledHolesPills = root.querySelectorAll('[data-cts-drilled-holes]');
    var selectCutStep = root.querySelector('[data-cts-step="select-cut"]');
    var cutGrid = root.querySelector('[data-cts-cut-grid]');
    var cutCaption = root.querySelector('[data-cts-cut-caption]');
    var summaryEl = root.querySelector('[data-cts-summary]');
    var summaryPartsEl = root.querySelector('[data-cts-summary-parts]');
    var summaryTotalEl = root.querySelector('[data-cts-summary-total]');
    var termsOkButton = root.querySelector('[data-cts-terms-ok]');
    var termsActionTextEl = root.querySelector('[data-cts-terms-action-text]');
    var instructionsCheckbox = root.querySelector('[data-cts-instructions-checkbox]');
    var instructionsInput = root.querySelector('[data-cts-instructions-input]');
    var addToCartButton = root.querySelector('[data-cts-add-to-cart]');

    var materialIndex = null;
    var currentProduct = null;
    var currentOptionPositions = null; // {thickness, length, width}
    var currentBounds = null; // {length, width, variant} - largest sheet for the selected thickness
    var currentMaterial = null;
    var currentColourLabel = null;
    var currentThickness = null;
    var currentShape = null; // 'rectangle' or 'circle'
    var currentSupportsExtras = false; // CTSShape tag present on the chosen colour
    var currentRoundedCorners = null; // 'no' or 'yes' - rectangle only
    var currentDrilledHoles = null; // 'no' or 'yes' - both shapes
    var currentCutMethod = null; // 'saw', 'cnc' or 'laser'
    var currentEdgeFinishOptions = []; // 'saw', 'milled', 'diamond', 'diamond-buff' - empty means no edge finish column
    var termsConfirmed = false; // re-locked on every renderPreviews() call, so it only ever reflects the *current* configuration

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function formatTemplate(template, bounds) {
      return template.replace('{length}', Math.round(bounds.length)).replace('{width}', Math.round(bounds.width));
    }

    function renderMaterialTiles() {
      materialGrid.innerHTML = '';
      Object.keys(materialIndex).forEach(function (material) {
        var tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'cts__tile';
        tile.setAttribute('aria-pressed', 'false');
        tile.textContent = material;
        tile.addEventListener('click', function () {
          selectMaterial(material, tile);
        });
        materialGrid.appendChild(tile);
      });
    }

    function selectMaterial(material, tile) {
      Array.prototype.forEach.call(materialGrid.children, function (el) {
        el.setAttribute('aria-pressed', String(el === tile));
      });

      currentMaterial = material;
      resetFrom('colour');
      colourStep.hidden = false;
      colourGrid.innerHTML = '';

      if (materialIndex[material].length === 0) {
        var emptyNote = document.createElement('p');
        emptyNote.className = 'cts__label';
        emptyNote.textContent = settings.labels.noColours;
        colourGrid.appendChild(emptyNote);
        return;
      }

      materialIndex[material].forEach(function (colour) {
        var tile2 = document.createElement('button');
        tile2.type = 'button';
        tile2.className = 'cts__tile';
        tile2.setAttribute('aria-pressed', 'false');

        var swatch = document.createElement('span');
        swatch.className = 'cts__tile-swatch';
        swatch.style.background = colour.swatch;
        tile2.appendChild(swatch);

        var label = document.createElement('span');
        label.textContent = colour.label;
        tile2.appendChild(label);

        tile2.addEventListener('click', function () {
          selectColour(colour, tile2);
        });
        colourGrid.appendChild(tile2);
      });
    }

    function selectColour(colour, tile) {
      Array.prototype.forEach.call(colourGrid.children, function (el) {
        el.setAttribute('aria-pressed', String(el === tile));
      });

      currentColourLabel = colour.label;
      resetFrom('thickness');
      clearError();

      fetch('/products/' + colour.handle + '.js')
        .then(function (response) {
          if (!response.ok) throw new Error('Could not load that product.');
          return response.json();
        })
        .then(function (product) {
          currentProduct = product;
          currentOptionPositions = {
            thickness: findOptionPosition(product.options, function (n) {
              return n.indexOf('thickness') !== -1;
            }),
            length: findOptionPosition(product.options, function (n) {
              return n.indexOf('length') !== -1;
            }),
            width: findOptionPosition(product.options, function (n) {
              return n.indexOf('width') !== -1;
            }),
          };

          if (!currentOptionPositions.thickness || !currentOptionPositions.length || !currentOptionPositions.width) {
            showError('This product is missing thickness/length/width options and cannot be used here.');
            return;
          }

          var thicknessKey = 'option' + currentOptionPositions.thickness;
          var thicknesses = [];
          product.variants.forEach(function (v) {
            if (thicknesses.indexOf(v[thicknessKey]) === -1) thicknesses.push(v[thicknessKey]);
          });
          thicknesses.sort(function (a, b) {
            return parseFloat(a) - parseFloat(b);
          });

          thicknessStep.hidden = false;
          thicknessGrid.innerHTML = '';
          thicknesses.forEach(function (thicknessValue) {
            var pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'cts__pill';
            pill.setAttribute('aria-pressed', 'false');
            pill.textContent = thicknessValue + 'mm';
            pill.addEventListener('click', function () {
              selectThickness(thicknessValue, pill);
            });
            thicknessGrid.appendChild(pill);
          });
        })
        .catch(function (err) {
          showError(err.message);
        });
    }

    function selectThickness(thicknessValue, pill) {
      Array.prototype.forEach.call(thicknessGrid.children, function (el) {
        el.setAttribute('aria-pressed', String(el === pill));
      });

      currentThickness = thicknessValue;
      currentBounds = largestSheetForThickness(
        currentProduct,
        currentOptionPositions.thickness,
        currentOptionPositions.length,
        currentOptionPositions.width,
        thicknessValue
      );

      resetFrom('shape');
      boundsNoteEl.textContent = formatTemplate(settings.labels.boundsNote, currentBounds);

      // Only materials opted in via the CTSShape tag get a choice of shape,
      // and the rounded corners / cut method / edge finish steps that
      // follow it - everyone else goes straight to entering sizes as a
      // rectangle with no extra steps, same as before any of this existed.
      currentSupportsExtras = currentProduct.tags.indexOf('CTSShape') !== -1;
      if (currentSupportsExtras) {
        shapeStep.hidden = false;
      } else {
        selectShape('rectangle', null);
      }
    }

    function selectShape(shape, tile) {
      Array.prototype.forEach.call(shapePills, function (el) {
        el.setAttribute('aria-pressed', String(el === tile));
      });

      currentShape = shape;

      resetFrom('rounded-corners');

      if (!currentSupportsExtras) {
        currentEdgeFinishOptions = [];
        partsStep.hidden = false;
        addRow();
        return;
      }

      if (shape === 'rectangle') {
        roundedCornersStep.hidden = false;
      } else {
        // Circle has no rounded-corners choice (no corners to round), so
        // it goes straight to Drilled holes - the next step both shapes
        // share, just before Select cut.
        drilledHolesStep.hidden = false;
      }
    }

    /**
     * Which cut methods are offered for the current shape/rounded-corners/
     * thickness combination. Laser is never offered at 10mm and over,
     * whatever else is chosen - saw cut only makes sense for straight,
     * unrounded edges, so it drops out as soon as rounded corners or a
     * circle are involved.
     */
    function eligibleCutMethods() {
      var isHighThickness = parseFloat(currentThickness) >= 10;

      if (currentShape === 'circle' || currentRoundedCorners === 'yes') {
        return isHighThickness ? ['cnc'] : ['cnc', 'laser'];
      }
      return isHighThickness ? ['saw', 'cnc'] : ['saw', 'cnc', 'laser'];
    }

    function cutMethodLabel(method) {
      if (method === 'saw') return settings.labels.sawCut;
      if (method === 'laser') return settings.labels.laserCut;
      return settings.labels.cncCut;
    }

    function cutMethodTooltip(method) {
      if (method === 'saw') return settings.labels.sawCutTooltip;
      if (method === 'laser') return settings.labels.laserCutTooltip;
      return settings.labels.cncCutTooltip;
    }

    function updateCutOptions() {
      cutGrid.innerHTML = '';
      currentCutMethod = null;
      if (cutCaption) cutCaption.textContent = ' ';

      eligibleCutMethods().forEach(function (method) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'cts__pill';
        pill.setAttribute('aria-pressed', 'false');
        pill.setAttribute('data-cts-tooltip', cutMethodTooltip(method));
        pill.textContent = cutMethodLabel(method);
        pill.addEventListener('click', function () {
          selectCutMethod(method, pill);
          if (cutCaption) cutCaption.textContent = cutMethodTooltip(method);
        });
        pill.addEventListener('mouseover', function () {
          if (cutCaption) cutCaption.textContent = cutMethodTooltip(method);
        });
        pill.addEventListener('focus', function () {
          if (cutCaption) cutCaption.textContent = cutMethodTooltip(method);
        });
        cutGrid.appendChild(pill);
      });
    }

    /**
     * Which edge finishes are offered for the current cut-method choice -
     * a per-part choice shown as a table column once Select cut is
     * answered, for both rounded-corners states. Empty for laser cut (no
     * edge finish offered at all); Saw edge is only ever offered alongside
     * a saw cut, since rounded corners (which rules out a saw cut - see
     * eligibleCutMethods) can only ever be CNC or laser.
     */
    function eligibleEdgeFinishes() {
      if (currentCutMethod === 'laser') return [];
      if (currentCutMethod === 'saw') return ['saw', 'milled', 'diamond', 'diamond-buff'];
      return ['milled', 'diamond', 'diamond-buff']; // cnc
    }

    function edgeFinishLabel(method) {
      if (method === 'saw') return settings.labels.sawEdge;
      if (method === 'milled') return settings.labels.milledEdge;
      if (method === 'diamond') return settings.labels.diamondPolish;
      return settings.labels.diamondPolishBuff; // 'diamond-buff'
    }

    function selectCutMethod(method, tile) {
      Array.prototype.forEach.call(cutGrid.children, function (el) {
        el.setAttribute('aria-pressed', String(el === tile));
      });
      currentCutMethod = method;

      resetRows();
      currentEdgeFinishOptions = eligibleEdgeFinishes();
      partsStep.hidden = false;
      addRow();
    }

    function selectRoundedCorners(value, tile) {
      Array.prototype.forEach.call(roundedCornersPills, function (el) {
        el.setAttribute('aria-pressed', String(el === tile));
      });
      currentRoundedCorners = value;
      resetFrom('drilled-holes');
      drilledHolesStep.hidden = false;
    }

    function selectDrilledHoles(value, tile) {
      Array.prototype.forEach.call(drilledHolesPills, function (el) {
        el.setAttribute('aria-pressed', String(el === tile));
      });
      currentDrilledHoles = value;
      resetFrom('select-cut');
      updateCutOptions();
      selectCutStep.hidden = false;
    }

    function resetFrom(stepName) {
      var steps = ['colour', 'thickness', 'shape', 'rounded-corners', 'drilled-holes', 'select-cut', 'parts'];
      var startIndex = steps.indexOf(stepName);
      for (var i = startIndex; i < steps.length; i++) {
        root.querySelector('[data-cts-step="' + steps[i] + '"]').hidden = true;
      }
      if (startIndex <= steps.indexOf('shape')) {
        currentShape = null;
        Array.prototype.forEach.call(shapePills, function (el) {
          el.setAttribute('aria-pressed', 'false');
        });
      }
      if (startIndex <= steps.indexOf('rounded-corners')) {
        currentRoundedCorners = null;
        Array.prototype.forEach.call(roundedCornersPills, function (el) {
          el.setAttribute('aria-pressed', 'false');
        });
      }
      if (startIndex <= steps.indexOf('drilled-holes')) {
        currentDrilledHoles = null;
        Array.prototype.forEach.call(drilledHolesPills, function (el) {
          el.setAttribute('aria-pressed', 'false');
        });
      }
      if (startIndex <= steps.indexOf('select-cut')) {
        currentCutMethod = null;
        cutGrid.innerHTML = '';
        if (cutCaption) cutCaption.textContent = ' ';
      }
      if (startIndex <= steps.indexOf('parts')) {
        currentEdgeFinishOptions = [];
        resetRows();
      }
      clearError();
    }

    /**
     * Back to a clean slate after a successful add to cart, so a customer
     * can start another Cut to Size item (different material/colour/
     * thickness) as its own separate cart line.
     */
    function resetToMaterial() {
      Array.prototype.forEach.call(materialGrid.children, function (el) {
        el.setAttribute('aria-pressed', 'false');
      });

      currentMaterial = null;
      currentColourLabel = null;
      currentThickness = null;
      currentProduct = null;
      currentOptionPositions = null;
      currentBounds = null;
      currentSupportsExtras = false;

      if (instructionsCheckbox) instructionsCheckbox.checked = false;
      if (instructionsInput) {
        instructionsInput.hidden = true;
        instructionsInput.value = '';
        instructionsInput.style.height = '';
      }

      resetFrom('colour');
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function resetRows() {
      rowsBody.innerHTML = '';
      previewsEl.innerHTML = '';
      summaryEl.hidden = true;
      addToCartButton.disabled = true;
    }

    function addRow() {
      var template = currentShape === 'circle' ? circleTemplate : rectangleTemplate;
      var fragment = template.content.cloneNode(true);

      var edgeFinishCell = fragment.querySelector('[data-cts-edge-finish-cell]');
      if (currentEdgeFinishOptions.length === 0) {
        edgeFinishCell.hidden = true;
      } else {
        var select = fragment.querySelector('[data-cts-field="edgeFinish"]');

        // A <select> always has something selected, defaulting to its
        // first option - without a blank placeholder, a customer could end
        // up charged for an edge finish they never actively chose. This
        // forces the same explicit choice every other step requires.
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = settings.labels.chooseEdgeFinish || 'Choose...';
        select.appendChild(placeholder);

        currentEdgeFinishOptions.forEach(function (method) {
          var option = document.createElement('option');
          option.value = method;
          option.textContent = edgeFinishLabel(method);
          select.appendChild(option);
        });
      }

      var cornersCell = fragment.querySelector('[data-cts-corners-cell]');
      if (cornersCell) {
        cornersCell.hidden = !cornersEnabled();
      }

      var holesCell = fragment.querySelector('[data-cts-holes-cell]');
      if (holesCell) {
        holesCell.hidden = !holesEnabled();
      }

      rowsBody.appendChild(fragment);
      refreshBadges();
      renderPreviews();
    }

    /**
     * Rounded corners is only offered for rectangle parts once the
     * top-level "Rounded corners?" step has been answered Yes.
     */
    function cornersEnabled() {
      return currentShape === 'rectangle' && currentRoundedCorners === 'yes';
    }

    /**
     * Updates a row's "Set corners" button to summarise its own popover
     * state (e.g. "2 · 30mm"), so the choice is visible without opening it
     * again. Falls back to the initial prompt text when nothing is chosen
     * yet.
     */
    function updateCornersSummary(cell) {
      if (!cell) return;
      var toggle = cell.querySelector('[data-cts-corners-toggle]');
      var count = countRoundedCorners(readCornersFromCell(cell));
      if (count === 0) {
        toggle.textContent = settings.labels.setCorners;
        return;
      }
      var radiusInput = cell.querySelector('[data-cts-field="cornerRadius"]');
      var radius = radiusInput ? radiusInput.value : '';
      toggle.textContent = count + ' corner' + (count > 1 ? 's' : '') + (radius ? ' · ' + radius + 'mm' : '');
    }

    /**
     * The four corner toggle buttons' current state, read directly from
     * the DOM (their aria-pressed attribute is the source of truth, same
     * pattern as every other tile/pill on this page).
     */
    function readCornersFromCell(cell) {
      var corners = {};
      var buttons = cell.querySelectorAll('[data-cts-corner]');
      Array.prototype.forEach.call(buttons, function (btn) {
        corners[btn.getAttribute('data-cts-corner')] = btn.getAttribute('aria-pressed') === 'true';
      });
      return corners;
    }

    function closeAllCornersPopovers() {
      var popovers = rowsBody.querySelectorAll('[data-cts-corners-popover]');
      Array.prototype.forEach.call(popovers, function (popover) {
        popover.hidden = true;
      });
    }

    /**
     * Shows a button's label (or, if present, its longer data-cts-tooltip
     * explanatory text - used by the Countersunk "Yes" pill) in a caption
     * line placed directly under that button's own group - a touch-friendly
     * stand-in for a hover tooltip (native title tooltips never fire on
     * touch devices). Corner/hole buttons update the shared caption below
     * the position grid; the Countersunk "Yes" pill updates its own
     * dedicated caption below the Yes/No buttons instead, since it sits in
     * a different part of the popover.
     */
    function updateCellCaption(button) {
      var popover = button.closest('.cts__corners-popover');
      if (!popover) return;
      var captionSelector = button.hasAttribute('data-cts-countersunk')
        ? '[data-cts-countersunk-caption]'
        : '[data-cts-corners-caption], [data-cts-holes-caption]';
      var caption = popover.querySelector(captionSelector);
      if (!caption) return;
      caption.textContent = button.getAttribute('data-cts-tooltip') || button.getAttribute('aria-label') || '';
    }

    function cornerLabel(key) {
      if (key === 'tl') return settings.labels.cornerTL;
      if (key === 'tr') return settings.labels.cornerTR;
      if (key === 'bl') return settings.labels.cornerBL;
      return settings.labels.cornerBR;
    }

    function selectedCornersText(corners) {
      return ['tl', 'tr', 'bl', 'br']
        .filter(function (key) {
          return corners && corners[key];
        })
        .map(cornerLabel)
        .join(', ');
    }

    /**
     * Drilled holes is offered for either shape once the top-level
     * "Drilled holes?" step has been answered Yes.
     */
    function holesEnabled() {
      return currentDrilledHoles === 'yes';
    }

    /**
     * Updates a row's "Set holes" button to summarise its own popover
     * state (e.g. "3 · ⌀8mm"), mirroring updateCornersSummary.
     */
    function updateHolesSummary(cell) {
      if (!cell) return;
      var toggle = cell.querySelector('[data-cts-holes-toggle]');
      var count = countSelectedHoles(readHolesFromCell(cell));
      if (count === 0) {
        toggle.textContent = settings.labels.setHoles;
        return;
      }
      var diameterInput = cell.querySelector('[data-cts-field="holeDiameter"]');
      var diameter = diameterInput ? diameterInput.value : '';
      toggle.textContent = count + ' hole' + (count > 1 ? 's' : '') + (diameter ? ' · ⌀' + diameter + 'mm' : '');
    }

    /**
     * The nine hole toggle buttons' current state, read directly from the
     * DOM (their aria-pressed attribute is the source of truth), same
     * pattern as readCornersFromCell.
     */
    function readHolesFromCell(cell) {
      var holes = {};
      var buttons = cell.querySelectorAll('[data-cts-hole]');
      Array.prototype.forEach.call(buttons, function (btn) {
        holes[btn.getAttribute('data-cts-hole')] = btn.getAttribute('aria-pressed') === 'true';
      });
      return holes;
    }

    function closeAllHolesPopovers() {
      var popovers = rowsBody.querySelectorAll('[data-cts-holes-popover]');
      Array.prototype.forEach.call(popovers, function (popover) {
        popover.hidden = true;
      });
    }

    function holeLabel(key) {
      if (key === 'tl') return settings.labels.holeTL;
      if (key === 'tm') return settings.labels.holeTM;
      if (key === 'tr') return settings.labels.holeTR;
      if (key === 'ml') return settings.labels.holeML;
      if (key === 'mc') return settings.labels.holeMC;
      if (key === 'mr') return settings.labels.holeMR;
      if (key === 'bl') return settings.labels.holeBL;
      if (key === 'bm') return settings.labels.holeBM;
      return settings.labels.holeBR;
    }

    function selectedHolesText(holes) {
      return HOLE_POSITIONS.filter(function (key) {
        return holes && holes[key];
      })
        .map(holeLabel)
        .join(', ');
    }

    function removeRow(button) {
      var row = button.closest('.cts__row');
      if (row) row.remove();
      refreshBadges();
      renderPreviews();
    }

    function refreshBadges() {
      var rows = rowsBody.querySelectorAll('.cts__row');
      for (var i = 0; i < rows.length; i++) {
        var badge = badgeForIndex(i);
        var badgeEl = rows[i].querySelector('[data-cts-badge]');
        badgeEl.textContent = badge.letter;
        badgeEl.style.background = badge.color;
      }
    }

    /**
     * Checks one dimension value against the minimum cut size, showing/
     * hiding its inline error span as needed. Shared between length,
     * width and diameter fields.
     */
    function checkMinCut(value, errorEl, minCut, minCutMessage) {
      var tooSmall = value > 0 && value < minCut;
      errorEl.hidden = !tooSmall;
      errorEl.textContent = tooSmall ? minCutMessage : '';
      return tooSmall;
    }

    function readRows() {
      var rows = rowsBody.querySelectorAll('.cts__row');
      var pieces = [];
      var minCut = settings.minCut;
      var minCutMessage = (settings.labels.minCutMessage || 'Minimum Cut {min}mm').replace('{min}', minCut);
      var cornerRadiusMessage = (settings.labels.cornerRadiusMessage || 'Radius must be between {min} and {max}mm')
        .replace('{min}', settings.cornerRadiusMin)
        .replace('{max}', settings.cornerRadiusMax);
      var holeDiameterMessage = (settings.labels.holeDiameterMessage || 'Diameter must be between {min} and {max}mm')
        .replace('{min}', settings.holeDiameterMin)
        .replace('{max}', settings.holeDiameterMax);
      var holeDistanceMessage = (settings.labels.holeDistanceMessage || 'Distance must be between {min} and {max}mm')
        .replace('{min}', settings.holeDistanceMin)
        .replace('{max}', settings.holeDistanceMax);
      var hasMinCutError = false;
      var isCircle = currentShape === 'circle';
      var cornersActive = cornersEnabled();
      var holesActive = holesEnabled();

      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var qty = parseInt(row.querySelector('[data-cts-field="qty"]').value, 10);
        var length, width, valid, tooBig;
        var rowIncomplete = false;
        var corners = null;
        var cornerRadius = null;
        var holes = null;
        var holeDiameter = null;
        var holeDistance = null;
        var holeCountersunk = null;

        // The edge finish dropdown always starts on a blank placeholder
        // (see addRow), so an unset value means the customer hasn't made
        // an explicit choice yet - required whenever the column exists at
        // all, exactly like every other step in this flow.
        var edgeFinishSelect = row.querySelector('[data-cts-field="edgeFinish"]');
        var edgeFinishChosen = currentEdgeFinishOptions.length === 0 || (edgeFinishSelect && edgeFinishSelect.value !== '');
        if (currentEdgeFinishOptions.length > 0) {
          edgeFinishSelect.classList.toggle('cts__input--invalid', !edgeFinishChosen);
        }

        if (isCircle) {
          var diameterInput = row.querySelector('[data-cts-field="diameter"]');
          var diameter = parseFloat(diameterInput.value);
          var diameterTooSmall = checkMinCut(diameter, row.querySelector('[data-cts-field-error="diameter"]'), minCut, minCutMessage);
          if (diameterTooSmall) hasMinCutError = true;

          var fitsBoundsCircle = diameter > 0 && qty > 0;
          if (fitsBoundsCircle && currentBounds) {
            fitsBoundsCircle = fitsWithRotation(diameter, diameter, currentBounds.length, currentBounds.width);
          }
          valid = fitsBoundsCircle && !diameterTooSmall && edgeFinishChosen;
          tooBig = !fitsBoundsCircle;

          diameterInput.classList.toggle('cts__input--invalid', diameter > 0 && (!fitsBoundsCircle || diameterTooSmall));

          length = diameter;
          width = diameter;

          rowIncomplete = !(diameter > 0 && qty > 0);
        } else {
          var lengthInput = row.querySelector('[data-cts-field="length"]');
          var widthInput = row.querySelector('[data-cts-field="width"]');
          length = parseFloat(lengthInput.value);
          width = parseFloat(widthInput.value);

          var lengthTooSmall = checkMinCut(length, row.querySelector('[data-cts-field-error="length"]'), minCut, minCutMessage);
          var widthTooSmall = checkMinCut(width, row.querySelector('[data-cts-field-error="width"]'), minCut, minCutMessage);
          if (lengthTooSmall || widthTooSmall) hasMinCutError = true;

          var fitsBoundsRect = length > 0 && width > 0 && qty > 0;
          if (fitsBoundsRect && currentBounds) {
            fitsBoundsRect = fitsWithRotation(length, width, currentBounds.length, currentBounds.width);
          }

          // Rounded corners is a per-row choice, only relevant once the
          // top-level "Rounded corners?" step is answered Yes - each row
          // must explicitly pick at least one corner and a valid radius,
          // same "no silent default" convention as edge finish above.
          var cornersValid = true;
          if (cornersActive) {
            var cornersCell = row.querySelector('[data-cts-corners-cell]');
            corners = readCornersFromCell(cornersCell);
            var anyCornerSelected = countRoundedCorners(corners) > 0;
            var radiusInput = cornersCell.querySelector('[data-cts-field="cornerRadius"]');
            var radiusValue = parseFloat(radiusInput.value);
            var radiusInRange = radiusValue >= settings.cornerRadiusMin && radiusValue <= settings.cornerRadiusMax;
            // Only show these once the customer has actually clicked Done
            // on this row's popover - not proactively the moment it opens.
            var cornersValidated = cornersCell.hasAttribute('data-cts-validated');

            var cornersError = row.querySelector('[data-cts-field-error="corners"]');
            var showCornersError = cornersValidated && !anyCornerSelected;
            cornersError.hidden = !showCornersError;
            cornersError.textContent = showCornersError ? settings.labels.cornersRequired : '';

            var radiusError = row.querySelector('[data-cts-field-error="cornerRadius"]');
            var showRadiusError = cornersValidated && anyCornerSelected && !radiusInRange;
            radiusError.hidden = !showRadiusError;
            radiusError.textContent = showRadiusError ? cornerRadiusMessage : '';

            cornersValid = anyCornerSelected && radiusInRange;
            cornerRadius = anyCornerSelected ? radiusValue : null;
            updateCornersSummary(cornersCell);
          }

          // fitsBoundsRect is only ever false here for a *complete* row
          // (never for a missing field, since incomplete rows are skipped
          // below) - so !fitsBoundsRect reliably means "too big for the
          // sheet", distinct from "too small", which has its own message.
          valid = fitsBoundsRect && !lengthTooSmall && !widthTooSmall && edgeFinishChosen && cornersValid;
          tooBig = !fitsBoundsRect;

          lengthInput.classList.toggle('cts__input--invalid', length > 0 && (!fitsBoundsRect || lengthTooSmall));
          widthInput.classList.toggle('cts__input--invalid', width > 0 && (!fitsBoundsRect || widthTooSmall));

          rowIncomplete = !(length > 0 && width > 0 && qty > 0);
        }

        // Drilled holes applies to both shapes, and - like corners for
        // rectangle above - is validated regardless of whether the row's
        // own dimension field is filled in yet (rowIncomplete is only
        // acted on further down, once holes has also had its turn) -
        // otherwise an empty Diameter field on a circle row would have
        // silently skipped holes validation for that row entirely, since
        // this code is shared and sits after both shape branches.
        // duplicated inside both the circle and rectangle branches.
        var holesValid = true;
        if (holesActive) {
          var holesCell = row.querySelector('[data-cts-holes-cell]');
          holes = readHolesFromCell(holesCell);
          var anyHoleSelected = countSelectedHoles(holes) > 0;
          var anyNonCentreHoleSelected = HOLE_POSITIONS.some(function (key) {
            return key !== 'mc' && holes[key];
          });

          var holeDiameterInput = holesCell.querySelector('[data-cts-field="holeDiameter"]');
          var holeDiameterValue = parseFloat(holeDiameterInput.value);
          var holeDiameterInRange = holeDiameterValue >= settings.holeDiameterMin && holeDiameterValue <= settings.holeDiameterMax;

          var holeDistanceInput = holesCell.querySelector('[data-cts-field="holeDistance"]');
          var holeDistanceValue = parseFloat(holeDistanceInput.value);
          var holeDistanceInRange = holeDistanceValue >= settings.holeDistanceMin && holeDistanceValue <= settings.holeDistanceMax;
          var holeDistanceNeeded = anyNonCentreHoleSelected;

          var countersunkButtons = holesCell.querySelectorAll('[data-cts-countersunk]');
          var countersunkChoice = null;
          Array.prototype.forEach.call(countersunkButtons, function (btn) {
            if (btn.getAttribute('aria-pressed') === 'true') countersunkChoice = btn.getAttribute('data-cts-countersunk');
          });

          // Only show these once the customer has actually clicked Done on
          // this row's popover - not proactively the moment it opens.
          var holesValidated = holesCell.hasAttribute('data-cts-validated');

          var showHolesError = holesValidated && !anyHoleSelected;
          var holesError = holesCell.querySelector('[data-cts-field-error="holes"]');
          holesError.hidden = !showHolesError;
          holesError.textContent = showHolesError ? settings.labels.holesRequired : '';

          var showHoleDiameterError = holesValidated && anyHoleSelected && !holeDiameterInRange;
          var holeDiameterError = holesCell.querySelector('[data-cts-field-error="holeDiameter"]');
          holeDiameterError.hidden = !showHoleDiameterError;
          holeDiameterError.textContent = showHoleDiameterError ? holeDiameterMessage : '';

          var showHoleDistanceError = holesValidated && holeDistanceNeeded && !holeDistanceInRange;
          var holeDistanceError = holesCell.querySelector('[data-cts-field-error="holeDistance"]');
          holeDistanceError.hidden = !showHoleDistanceError;
          holeDistanceError.textContent = showHoleDistanceError ? holeDistanceMessage : '';

          var showCountersunkError = holesValidated && anyHoleSelected && !countersunkChoice;
          var countersunkError = holesCell.querySelector('[data-cts-field-error="countersunk"]');
          countersunkError.hidden = !showCountersunkError;
          countersunkError.textContent = showCountersunkError ? settings.labels.countersunkRequired : '';

          holesValid = anyHoleSelected && holeDiameterInRange && (!holeDistanceNeeded || holeDistanceInRange) && !!countersunkChoice;
          holeDiameter = anyHoleSelected ? holeDiameterValue : null;
          holeDistance = anyNonCentreHoleSelected ? holeDistanceValue : null;
          holeCountersunk = anyHoleSelected ? countersunkChoice : null;
          updateHolesSummary(holesCell);
        }
        valid = valid && holesValid;

        // Deferred from the shape branches above so holes validation (just
        // above) always runs first, regardless of whether this row's own
        // dimension field is filled in yet.
        if (rowIncomplete) continue;

        var edgeFinish = edgeFinishSelect && edgeFinishSelect.value ? edgeFinishSelect.value : null;

        pieces.push({
          length: length,
          width: width,
          qty: qty,
          valid: valid,
          tooBig: tooBig,
          shape: currentShape,
          edgeFinish: edgeFinish,
          corners: corners,
          cornerRadius: cornerRadius,
          holes: holes,
          holeDiameter: holeDiameter,
          holeDistance: holeDistance,
          holeCountersunk: holeCountersunk,
          badge: badgeForIndex(i),
        });
      }

      addRowButton.disabled = hasMinCutError;
      return pieces;
    }

    /**
     * Confirming the "bespoke, non-returnable" terms is only ever valid for
     * the configuration it was clicked against - any recalculation (any
     * edit to any part) re-locks it, requiring a fresh click before Add to
     * cart will work again. renderPreviews() runs on every such edit, so
     * resetting here (rather than tracking each individual field) covers
     * every case that should invalidate it.
     */
    function resetTermsConfirmation() {
      termsConfirmed = false;
      termsOkButton.setAttribute('aria-pressed', 'false');
      termsActionTextEl.hidden = false;
    }

    function renderPreviews() {
      resetTermsConfirmation();
      var pieces = readRows();
      previewsEl.innerHTML = '';

      var anyTooBig = pieces.some(function (p) {
        return p.tooBig;
      });
      if (anyTooBig && currentBounds) {
        showError(formatTemplate(settings.labels.outOfBounds, currentBounds));
      } else {
        clearError();
      }

      var validPieces = pieces.filter(function (p) {
        return p.valid;
      });
      if (validPieces.length === 0) {
        summaryEl.hidden = true;
        addToCartButton.disabled = true;
        return;
      }

      // Scale each preview relative to the largest *entered* part, not the
      // stock sheet - a 500x500 part against a 2500x1250 sheet would
      // otherwise render as a barely-visible 28x28px square.
      var maxDimension = Math.max.apply(
        null,
        validPieces.map(function (p) {
          return Math.max(p.length, p.width);
        })
      );
      var scale = PREVIEW_MAX_PX / maxDimension;

      validPieces.forEach(function (piece) {
          var card = document.createElement('div');
          card.className = 'cts__preview-card';

          var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          var pxW = piece.length * scale;
          var pxH = piece.width * scale;
          var isCircle = piece.shape === 'circle';
          // Reserves space for the dimension lines/labels above (both
          // shapes) and to the left (rectangle's width line only - a
          // circle only ever shows one diameter line, per the design).
          var marginTop = 22;
          var marginLeft = isCircle ? 0 : 22;
          var totalW = pxW + marginLeft;
          var totalH = pxH + marginTop;
          svg.setAttribute('class', 'cts__preview-svg');
          svg.setAttribute('viewBox', '0 0 ' + totalW + ' ' + totalH);
          svg.setAttribute('width', totalW);
          svg.setAttribute('height', totalH);

          // Everything that draws the piece itself (shape/holes/label)
          // stays in its own untouched 0,0-origin coordinate space inside
          // this translated group - only the dimension lines below need
          // to reason about the margin directly.
          var shapeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          shapeGroup.setAttribute('transform', 'translate(' + marginLeft + ', ' + marginTop + ')');

          if (isCircle) {
            var circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            circle.setAttribute('cx', pxW / 2);
            circle.setAttribute('cy', pxH / 2);
            circle.setAttribute('r', Math.min(pxW, pxH) / 2);
            circle.setAttribute('fill', piece.badge.color);
            shapeGroup.appendChild(circle);
          } else {
            // Square by default; a corner is only drawn rounded when the
            // customer actually picked it in that row's corner popover -
            // the preview should reflect what's really being cut, not a
            // uniform cosmetic rounding regardless of the choice made.
            var corners = piece.corners;
            var radiusPx = piece.cornerRadius ? Math.min(Math.max(4, piece.cornerRadius * scale), pxW / 2, pxH / 2) : 0;
            var rectPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            rectPath.setAttribute(
              'd',
              roundedRectPath(
                pxW,
                pxH,
                corners && corners.tl ? radiusPx : 0,
                corners && corners.tr ? radiusPx : 0,
                corners && corners.br ? radiusPx : 0,
                corners && corners.bl ? radiusPx : 0
              )
            );
            rectPath.setAttribute('fill', piece.badge.color);
            shapeGroup.appendChild(rectPath);
          }

          var holePositions = holePositionsPx(piece, pxW, pxH, scale);
          Object.keys(holePositions).forEach(function (key) {
            var pos = holePositions[key];
            var marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            marker.setAttribute('cx', pos.x);
            marker.setAttribute('cy', pos.y);
            marker.setAttribute('r', 4);
            marker.setAttribute('fill', '#1a1a1a');
            marker.setAttribute('stroke', '#ffffff');
            marker.setAttribute('stroke-width', 1);
            shapeGroup.appendChild(marker);
          });

          var label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          label.setAttribute('class', 'cts__piece-label');
          label.setAttribute('x', pxW / 2);
          // A centre hole marker sits at the exact same point as the label
          // by default - offset the label up so it stays legible when one
          // is selected.
          label.setAttribute('y', piece.holes && piece.holes.mc ? pxH / 2 - 14 : pxH / 2);
          label.textContent = piece.badge.letter + ' ' + formatDimensions(piece);
          shapeGroup.appendChild(label);

          svg.appendChild(shapeGroup);

          // Dimension lines - length (or diameter, for a circle) along the
          // top, width down the left side for rectangles only. Drawn in
          // the svg's own top-level (untranslated) coordinate space, since
          // they live in the reserved margin rather than over the shape.
          appendDimensionLine(svg, marginLeft, 16, marginLeft + pxW, 16, Math.round(piece.length) + 'mm', false, 9);
          if (!isCircle) {
            appendDimensionLine(svg, 16, marginTop, 16, marginTop + pxH, Math.round(piece.width) + 'mm', true, 9);
          }

          card.appendChild(svg);

          var caption = document.createElement('p');
          caption.className = 'cts__label';
          caption.textContent = 'Qty ' + piece.qty;
          card.appendChild(caption);

          previewsEl.appendChild(card);
        });

      var rate = ratePerM2(currentBounds, settings.upliftPercent);
      if (!rate) {
        summaryEl.hidden = true;
        addToCartButton.disabled = true;
        return;
      }

      var breakdown;
      try {
        breakdown = computePriceBreakdown(validPieces, rate);
      } catch (err) {
        breakdown = null;
      }
      if (!breakdown) {
        summaryEl.hidden = true;
        addToCartButton.disabled = true;
        return;
      }

      summaryPartsEl.innerHTML = '';
      validPieces.forEach(function (piece, index) {
        var line = document.createElement('div');
        line.className = 'cts__summary-line';

        var label = document.createElement('span');
        var labelText = piece.badge.letter + ' ' + piece.qty + '× ' + formatDimensions(piece) + 'mm';
        if (piece.edgeFinish) labelText += ' · ' + edgeFinishLabel(piece.edgeFinish);
        var cornerCount = countRoundedCorners(piece.corners);
        if (cornerCount > 0) labelText += ' · ' + cornerCount + ' rounded corner' + (cornerCount > 1 ? 's' : '');
        var holeCount = countSelectedHoles(piece.holes);
        if (holeCount > 0) labelText += ' · ' + holeCount + ' hole' + (holeCount > 1 ? 's' : '');
        label.textContent = labelText;
        line.appendChild(label);

        var price = document.createElement('span');
        price.textContent = formatGBP(breakdown.allocatedPencePerPart[index] / 100);
        line.appendChild(price);

        summaryPartsEl.appendChild(line);
      });

      summaryTotalEl.textContent = formatGBP(breakdown.chargedPence / 100);
      summaryEl.hidden = false;
      addToCartButton.disabled = !extrasComplete() || !termsConfirmed;
    }

    /**
     * Whether every required Perspex-only extra (rounded corners, for
     * rectangle only; drilled holes, for both shapes; and cut method) has
     * been answered - materials without CTSShape have no extras to
     * complete, so this is trivially true for them, same as before this
     * feature existed.
     */
    function extrasComplete() {
      if (!currentSupportsExtras) return true;
      if (currentShape === 'rectangle' && currentRoundedCorners === null) return false;
      if (currentDrilledHoles === null) return false;
      return currentCutMethod !== null;
    }

    function formatGBP(amount) {
      return '£' + amount.toFixed(2);
    }

    /**
     * A piece's size as shown to the customer: "L×W" for a rectangle
     * (length and width may differ), "⌀D" for a circle (length and width
     * are the same diameter internally - see readRows).
     */
    function formatDimensions(piece) {
      if (piece.shape === 'circle') return '⌀' + Math.round(piece.length);
      return Math.round(piece.length) + '×' + Math.round(piece.width);
    }

    /**
     * Full price breakdown for the currently entered valid parts - the
     * cart item(s) the ladder rounds the raw total to, and each part's
     * share of that same rounded total (see CutToSizeCharge.allocatePence).
     * Used identically by the live on-page summary and the actual
     * add-to-cart call, so what's shown on the page can never drift from
     * what's actually charged. Returns null if there's nothing priceable
     * yet; throws if the ladder has no variant covering the total.
     */
    function computePriceBreakdown(validPieces, rate) {
      var thicknessValue = parseFloat(currentThickness);
      var cornerSettings = {
        minThickness: settings.cornerRateMinThickness,
        maxThickness: settings.cornerRateMaxThickness,
        minRate: settings.cornerRateMin,
        maxRate: settings.cornerRateMax,
      };
      var holeSettings = {
        minThickness: settings.holeRateMinThickness,
        maxThickness: settings.holeRateMaxThickness,
        minRate: settings.holeRateMin,
        maxRate: settings.holeRateMax,
      };
      var rawPricesGBP = validPieces.map(function (piece) {
        return (
          piecePriceGBP(piece, rate) +
          edgeFinishPriceGBP(piece, thicknessValue, settings.diamondPolishRate, settings.diamondPolishBuffRate) +
          roundedCornersPriceGBP(piece, thicknessValue, cornerSettings) +
          drilledHolesPriceGBP(piece, thicknessValue, holeSettings)
        );
      });
      var totalGBP = rawPricesGBP.reduce(function (sum, price) {
        return sum + price;
      }, 0);

      var cartItems = window.CutToSizeCharge.pickCutToSizeCartItems(totalGBP, window.CutToSizeChargeTable);
      if (cartItems.length === 0) return null;

      var chargedPence = window.CutToSizeCharge.totalChargedPence(cartItems);
      var rawPencePerPart = rawPricesGBP.map(function (priceGBP) {
        return priceGBP * 100;
      });
      var allocatedPencePerPart = window.CutToSizeCharge.allocatePence(rawPencePerPart, chargedPence);

      return {
        cartItems: cartItems,
        chargedPence: chargedPence,
        allocatedPencePerPart: allocatedPencePerPart,
      };
    }

    /**
     * One 'Part N' property per valid entered piece - dimensions, quantity
     * and that piece's own price, so it's clear from the cart/order exactly
     * what was ordered and what each part cost, even though they're all
     * charged together as a single line. Prices are the ladder-allocated
     * whole-penny amounts (see computePriceBreakdown), not each piece's raw
     * price, so they always sum to exactly the total the customer is
     * charged.
     */
    function buildPartProperties(validPieces, allocatedPencePerPart) {
      var properties = {};
      validPieces.forEach(function (piece, index) {
        var text = piece.qty + '× ' + formatDimensions(piece) + 'mm';
        if (piece.edgeFinish) text += ' — ' + edgeFinishLabel(piece.edgeFinish);
        if (countRoundedCorners(piece.corners) > 0) {
          text += ' — Rounded corners: ' + selectedCornersText(piece.corners) + ' (' + piece.cornerRadius + 'mm radius)';
        }
        if (countSelectedHoles(piece.holes) > 0) {
          var holeDetail = '⌀' + piece.holeDiameter + 'mm';
          if (piece.holeDistance !== null) holeDetail += ', ' + piece.holeDistance + 'mm from edge';
          holeDetail += piece.holeCountersunk === 'yes' ? ', countersunk' : ', not countersunk';
          text += ' — Drilled holes: ' + selectedHolesText(piece.holes) + ' (' + holeDetail + ')';
        }
        text += ' — ' + formatGBP(allocatedPencePerPart[index] / 100);
        properties['Part ' + (index + 1)] = text;
      });
      return properties;
    }

    function addToCart() {
      clearError();

      var pieces = readRows();
      var validPieces = pieces.filter(function (p) {
        return p.valid;
      });
      if (validPieces.length === 0 || !currentBounds || !currentBounds.variant) {
        showError('Please enter at least one valid part before adding to cart.');
        return;
      }

      if (!extrasComplete()) {
        showError('Please choose rounded corners and a cut method before adding to cart.');
        return;
      }

      var rate = ratePerM2(currentBounds, settings.upliftPercent);
      if (!rate) {
        showError('Could not calculate a price for this selection.');
        return;
      }

      var breakdown;
      try {
        breakdown = computePriceBreakdown(validPieces, rate);
      } catch (err) {
        showError(err.message);
        return;
      }
      if (!breakdown) {
        showError('Could not calculate a price for this selection.');
        return;
      }

      var properties = {
        Material: currentMaterial,
        Colour: currentColourLabel,
        Thickness: currentThickness + 'mm',
        Shape: currentShape === 'circle' ? 'Circle' : 'Rectangle',
        Product: currentProduct.title + ' (' + currentBounds.variant.sku + ')',
        'Terms agreed': 'Yes',
      };
      if (currentShape === 'rectangle' && currentRoundedCorners) {
        properties['Rounded corners'] = currentRoundedCorners === 'yes' ? settings.labels.roundedCornersYes : settings.labels.roundedCornersNo;
      }
      if (currentDrilledHoles) {
        properties['Drilled holes'] = currentDrilledHoles === 'yes' ? settings.labels.drilledHolesYes : settings.labels.drilledHolesNo;
      }
      if (currentCutMethod) {
        properties['Cut method'] = cutMethodLabel(currentCutMethod);
      }
      var partProperties = buildPartProperties(validPieces, breakdown.allocatedPencePerPart);
      for (var key in partProperties) {
        if (Object.prototype.hasOwnProperty.call(partProperties, key)) properties[key] = partProperties[key];
      }

      // Added last, deliberately - carries through to the cart/checkout as
      // the final property on the line, after every part's own details.
      if (instructionsCheckbox && instructionsCheckbox.checked && instructionsInput) {
        var instructionsValue = instructionsInput.value.trim();
        if (instructionsValue) {
          properties[settings.labels.instructionsProperty || 'Additional instructions'] = instructionsValue;
        }
      }

      var items = breakdown.cartItems.map(function (item) {
        return { id: item.id, quantity: item.quantity, properties: properties };
      });

      addToCartButton.disabled = true;

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (body) {
              throw new Error(body.description || body.message || 'Could not add this item to the cart.');
            });
          }
          return response.json();
        })
        .then(function () {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
          // resetToMaterial leaves the button correctly disabled (nothing
          // valid entered yet in the fresh form), so it's not re-enabled
          // here the way the catch path below re-enables it to retry.
          resetToMaterial();
        })
        .catch(function (err) {
          showError(err.message);
          addToCartButton.disabled = false;
        });
    }

    Array.prototype.forEach.call(shapePills, function (pill) {
      pill.addEventListener('click', function () {
        selectShape(pill.getAttribute('data-cts-shape'), pill);
      });
    });

    Array.prototype.forEach.call(roundedCornersPills, function (pill) {
      pill.addEventListener('click', function () {
        selectRoundedCorners(pill.getAttribute('data-cts-rounded-corners'), pill);
      });
    });

    Array.prototype.forEach.call(drilledHolesPills, function (pill) {
      pill.addEventListener('click', function () {
        selectDrilledHoles(pill.getAttribute('data-cts-drilled-holes'), pill);
      });
    });

    addToCartButton.addEventListener('click', addToCart);
    termsOkButton.addEventListener('click', function () {
      termsConfirmed = true;
      termsOkButton.setAttribute('aria-pressed', 'true');
      termsActionTextEl.hidden = true;
      addToCartButton.disabled = !extrasComplete();
    });

    function resizeInstructionsInput() {
      if (!instructionsInput) return;
      instructionsInput.style.height = 'auto';
      instructionsInput.style.height = instructionsInput.scrollHeight + 'px';
    }

    if (instructionsCheckbox && instructionsInput) {
      instructionsCheckbox.addEventListener('change', function () {
        instructionsInput.hidden = !instructionsCheckbox.checked;
        if (!instructionsCheckbox.checked) {
          instructionsInput.value = '';
          instructionsInput.style.height = '';
        } else {
          resizeInstructionsInput();
        }
      });
      instructionsInput.addEventListener('input', resizeInstructionsInput);
    }

    addRowButton.addEventListener('click', addRow);
    rowsBody.addEventListener('click', function (event) {
      if (event.target.closest('[data-cts-remove-row]')) {
        removeRow(event.target);
        return;
      }

      var cornerButton = event.target.closest('[data-cts-corner]');
      if (cornerButton) {
        var pressed = cornerButton.getAttribute('aria-pressed') === 'true';
        cornerButton.setAttribute('aria-pressed', String(!pressed));
        updateCellCaption(cornerButton);
        renderPreviews();
        return;
      }

      var cornersToggle = event.target.closest('[data-cts-corners-toggle]');
      if (cornersToggle) {
        var popover = cornersToggle.closest('[data-cts-corners-cell]').querySelector('[data-cts-corners-popover]');
        var wasOpen = !popover.hidden;
        closeAllCornersPopovers();
        popover.hidden = wasOpen;
        return;
      }

      if (event.target.closest('[data-cts-corners-done]')) {
        var cornersCellDone = event.target.closest('[data-cts-corners-cell]');
        // Marks this row's popover as "an attempt to confirm has been
        // made", so readRows() starts showing its error messages (it
        // deliberately doesn't show them just because the popover is
        // open - only once Done has actually been clicked once).
        cornersCellDone.setAttribute('data-cts-validated', 'true');
        renderPreviews();
        // Only close if that attempt actually succeeded - otherwise the
        // customer would never see the error message it just revealed.
        var cornersStillInvalid =
          !cornersCellDone.querySelector('[data-cts-field-error="corners"]').hidden ||
          !cornersCellDone.querySelector('[data-cts-field-error="cornerRadius"]').hidden;
        if (!cornersStillInvalid) closeAllCornersPopovers();
        return;
      }

      var holeButton = event.target.closest('[data-cts-hole]');
      if (holeButton) {
        var holePressed = holeButton.getAttribute('aria-pressed') === 'true';
        holeButton.setAttribute('aria-pressed', String(!holePressed));
        updateCellCaption(holeButton);
        renderPreviews();
        return;
      }

      var countersunkButton = event.target.closest('[data-cts-countersunk]');
      if (countersunkButton) {
        var countersunkGroup = countersunkButton.closest('[data-cts-holes-popover]').querySelectorAll('[data-cts-countersunk]');
        Array.prototype.forEach.call(countersunkGroup, function (btn) {
          btn.setAttribute('aria-pressed', String(btn === countersunkButton));
        });
        updateCellCaption(countersunkButton);
        renderPreviews();
        return;
      }

      var holesToggle = event.target.closest('[data-cts-holes-toggle]');
      if (holesToggle) {
        var holesPopover = holesToggle.closest('[data-cts-holes-cell]').querySelector('[data-cts-holes-popover]');
        var holesWasOpen = !holesPopover.hidden;
        closeAllHolesPopovers();
        holesPopover.hidden = holesWasOpen;
        return;
      }

      if (event.target.closest('[data-cts-holes-done]')) {
        var holesCellDone = event.target.closest('[data-cts-holes-cell]');
        holesCellDone.setAttribute('data-cts-validated', 'true');
        renderPreviews();
        var holesStillInvalid =
          !holesCellDone.querySelector('[data-cts-field-error="holes"]').hidden ||
          !holesCellDone.querySelector('[data-cts-field-error="holeDiameter"]').hidden ||
          !holesCellDone.querySelector('[data-cts-field-error="holeDistance"]').hidden ||
          !holesCellDone.querySelector('[data-cts-field-error="countersunk"]').hidden;
        if (!holesStillInvalid) closeAllHolesPopovers();
      }
    });
    rowsBody.addEventListener('input', renderPreviews);
    // Evergreen browsers already fire 'input' for a <select> too, but
    // 'change' is added as a belt-and-braces fallback for the edge finish
    // dropdown - harmless if both fire, since renderPreviews is idempotent.
    rowsBody.addEventListener('change', renderPreviews);

    // Mouse hover and keyboard focus also update the caption (mouseover/
    // focusin both bubble, unlike mouseenter/focus, so a single delegated
    // listener each covers every corner/hole button without binding one
    // per button) - tapping already updates it via the click handler above.
    rowsBody.addEventListener('mouseover', function (event) {
      var button = event.target.closest('[data-cts-corner], [data-cts-hole], [data-cts-tooltip]');
      if (button) updateCellCaption(button);
    });
    rowsBody.addEventListener('focusin', function (event) {
      var button = event.target.closest('[data-cts-corner], [data-cts-hole], [data-cts-tooltip]');
      if (button) updateCellCaption(button);
    });

    // Closes any open corner/hole popover when clicking anywhere outside
    // it - standard popover behaviour, not tied to a specific row.
    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-cts-corners-cell]')) closeAllCornersPopovers();
      if (!event.target.closest('[data-cts-holes-cell]')) closeAllHolesPopovers();
    });

    fetch(settings.indexUrl)
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        materialIndex = data;
        renderMaterialTiles();
      })
      .catch(function () {
        showError('Could not load the list of materials. Please refresh and try again.');
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
